import api from './api';

// V1 lib_editrecord.pl::driverpayaddition / savedriverpayaddition port.
// All money fields are plain numbers (the backend stores Decimal(10,2)).
// All dates use the `mm/dd/yyyy` shape the rest of the Pro 360 UI uses.

export interface DriverPayAddition {
  recno: number;
  pronumber: number;
  type: string;
  driver: string;
  eventDate: string;
  origCode: number;
  destCode: number;
  origName: string;
  destName: string;
  rate: number;
  /** #33649 — the driverpay_rates row this line's rate came from, as STORED.
   *  Read-only, and not a reliable MISSING RATE signal on its own: additions
   *  created through /pro before #33649 never persisted it. */
  rateRecno: number;
  /** #33741 — stored rateRecno, else the engine's live verdict for the lane.
   *  0 on a BOBTAIL means MISSING RATE: no zone rate exists, so the line cannot
   *  be approved (the backend rejects payStatus='PAY'). Read-only. */
  effectiveRateRecno: number;
  fuelType: string;
  description: string;
  payStatus: string;
  approvedBy: string;
  approvedDatetime: string;
  payrollDate: string;
  deleted: string;
  gwiCommission: string;
  donotpayReason: string;
  intime: string;
  outtime: string;
  hours: number;
  /** True once this addition's payroll has been invoiced — the row is locked
   *  from edits and deletes (backend rejects with 409). Read-only, derived. */
  isPaid: boolean;
  /** Present only on the create response for a Bobtail / Waiting Time addition:
   *  the outcome of the auto-add of the matching customer invoice line item.
   *  Absent/null for other types, updates, and fetched rows. Lets the modal
   *  toast the real result (V1 savedriverpayaddition:4459-4587).
   *   - 'added'          — line item written to the open invoice
   *   - 'trouble_ticket' — invoice already closed; a trouble ticket was raised
   *   - 'no_invoice'     — the order has no open (primary) invoice
   *   - 'cap'            — the invoice already has the max (15) line items */
  invoiceLineItemOutcome?: 'added' | 'trouble_ticket' | 'no_invoice' | 'cap' | null;
  /** Display label of the invoice item (e.g. "Waiting Time"), when applicable. */
  invoiceLineItemLabel?: string | null;
}

/** Patch shape — every field is optional so callers can send only changed
 * values. The backend diffs against the existing row and only writes the
 * delta. `limitOverride` (+ optional reason) is set only when the user
 * confirmed a rate over the type's limit; the backend allows the save and
 * logs the override to the Pro History tab. */
export type DriverPayAdditionPatch = Partial<Omit<DriverPayAddition,
  'recno' | 'pronumber' | 'origName' | 'destName' | 'approvedBy' |
  'approvedDatetime' | 'payrollDate' | 'isPaid' | 'rateRecno' |
  'effectiveRateRecno'
>> & {
  limitOverride?: boolean;
  limitOverrideReason?: string;
};

export interface DriverPayAdditionDriverOption {
  isMule?: boolean;
  unitnumber: string;
  name: string;
  /** unit.status — IN SERVICE, OUT OF SERVICE, etc. Shown as a hint after
   *  the name in the typeahead when not IN SERVICE. */
  status: string;
  /** V1 lib_editrecord.pl:3622-3638 — when a driver is hourly (company
   *  driver) the addition's paystatus must be set to DO NOT PAY. */
  payrollHourly: boolean;
}

export interface RateLookupResponse {
  type: string;
  rate: number;
  /** Non-null only for WAITING TIME types — the per-hour rate. The amount is
   *  derived client-side via calctime (hours * perrate). */
  perrate: number | null;
  fuelType: string;
  raterecno: number;
  paystatus: string;
  /** True when the type doesn't support auto-rate; UI should not overwrite
   *  the user's typed value. */
  noAutoRate?: boolean;
}

export type DriverPayAdditionVariant = 'drayage' | 'waiting' | 'default';

export interface DriverPayAdditionTypeOption {
  name: string;
  variant: DriverPayAdditionVariant;
  showsFuelType: boolean;
  gwiCommission: boolean;
  limitAmount: number;
  sortOrder: number;
  /** Optional help text shown under the Type dropdown in the modal (V1's
   *  %typedescription). Empty/absent → nothing shown. */
  description?: string;
  /** True for types returned by the manager-types endpoint — only merged into
   *  the dropdown for users whose 360 group is granted that endpoint. */
  managerOnly?: boolean;
}

export interface DriverPayAdditionReferenceData {
  drivers: DriverPayAdditionDriverOption[];
  additionTypes: DriverPayAdditionTypeOption[];
  payStatuses: string[];
  donotpayReasons: string[];
  fuelTypes: string[];
  deletedOptions: string[];
  /** Distinct locations touched by this pro's legs (origins + destinations,
   *  deduped), each with a display address — matches V1 buildleglocations_data.
   *  Powers the WAITING TIME Location dropdown. */
  legLocations: Array<{
    code: number;
    name: string;
    addr: string;
  }>;
}

export const driverPayAdditionsService = {
  /** Preview fuel through the existing lookup route without auto-rating the addition. */
  async lookupFuelPercent(
    pronumber: number,
    params: { eventDate: string; fuelType: string },
  ): Promise<number> {
    const res = await api.get(`/orders/${pronumber}/driver-pay-additions/rate-lookup`, {
      params: { type: '', ...params },
    });
    const percent: unknown = res.data.fuelPercent;
    if (typeof percent !== 'number' || !Number.isFinite(percent)) {
      throw new Error('Fuel preview unavailable');
    }
    return percent;
  },

  async fetchReferenceData(pronumber: number): Promise<DriverPayAdditionReferenceData> {
    const res = await api.get(`/orders/${pronumber}/driver-pay-additions/reference-data`);
    return res.data;
  },

  /** Manager-only addition types (e.g. BONUS). RBAC-gated: resolves to [] when
   *  the caller's 360 group isn't granted the endpoint (403) so the modal
   *  simply omits them. The caller merges these into the Type dropdown. */
  async fetchManagerAdditionTypes(pronumber: number): Promise<DriverPayAdditionTypeOption[]> {
    try {
      const res = await api.get(`/orders/${pronumber}/driver-pay-additions/manager/addition-types`);
      return (res.data?.additionTypes ?? []) as DriverPayAdditionTypeOption[];
    } catch {
      return [];
    }
  },

  async lookupRate(
    pronumber: number,
    params: { type: string; eventDate?: string; origCode?: number; destCode?: number },
  ): Promise<RateLookupResponse> {
    const res = await api.get(`/orders/${pronumber}/driver-pay-additions/rate-lookup`, {
      params: {
        type: params.type,
        eventDate: params.eventDate ?? '',
        origCode: params.origCode ?? 0,
        destCode: params.destCode ?? 0,
      },
    });
    return res.data;
  },

  async fetchAddition(pronumber: number, recno: number): Promise<DriverPayAddition> {
    const res = await api.get(`/orders/${pronumber}/driver-pay-additions/${recno}`);
    return res.data;
  },

  async createAddition(pronumber: number, payload: DriverPayAdditionPatch): Promise<DriverPayAddition> {
    const res = await api.post(`/orders/${pronumber}/driver-pay-additions`, payload);
    return res.data;
  },

  async updateAddition(pronumber: number, recno: number, payload: DriverPayAdditionPatch): Promise<DriverPayAddition> {
    const res = await api.put(`/orders/${pronumber}/driver-pay-additions/${recno}`, payload);
    return res.data;
  },

  async deleteAddition(pronumber: number, recno: number): Promise<{ ok: boolean; recno: number }> {
    const res = await api.delete(`/orders/${pronumber}/driver-pay-additions/${recno}`);
    return res.data;
  },
};
